import { clsx } from 'clsx';
import type { ComponentProps, ReactNode } from 'react';

export type CardProps = ComponentProps<'section'>;

/** White bordered surface. Compose with CardHeader / CardBody. */
export function Card({ className, ...rest }: CardProps) {
  return <section className={clsx('rounded-lg border border-slate-200 bg-surface shadow-xs', className)} {...rest} />;
}

export type CardHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned controls (buttons, menus). */
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
};

export function CardHeader({ title, description, actions, icon, className }: CardHeaderProps) {
  return (
    <header className={clsx('flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3', className)}>
      <div className="flex min-w-0 items-start gap-2">
        {icon && <span className="mt-0.5 text-slate-400">{icon}</span>}
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </header>
  );
}

export type CardBodyProps = ComponentProps<'div'> & { padded?: boolean };

export function CardBody({ className, padded = true, ...rest }: CardBodyProps) {
  return <div className={clsx(padded && 'p-4', className)} {...rest} />;
}
